import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "text-lg font-medium text-gray-900" }
const _hoisted_2 = { class: "space-y-6" }
const _hoisted_3 = { class: "space-y-4" }
const _hoisted_4 = { class: "flex items-center gap-4" }
const _hoisted_5 = { class: "text-sm text-gray-500" }
const _hoisted_6 = {
  key: 0,
  class: "space-y-2"
}
const _hoisted_7 = { class: "border border-gray-200 rounded-lg p-4 bg-gray-50" }
const _hoisted_8 = ["src"]
const _hoisted_9 = { class: "bg-blue-50 border border-blue-200 rounded-lg p-4" }
const _hoisted_10 = { class: "flex items-center justify-between" }
const _hoisted_11 = { class: "text-sm text-gray-600" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = {
  key: 0,
  class: "fas fa-spinner fa-spin mr-2"
}

import { ref, watch } from 'vue'
import ModalComp from '@/components/ui/ModalComp.vue'
import ProductAutocomplete from './ProductAutocomplete.vue'
import MultilingualTextarea from './MultilingualTextarea.vue'
import type { LookbookFicheDisplay } from '@/types/lookbook-fiche'
import type { Produit } from '@/types/produit'
import { getFicheProduits } from '@/api/lookbooks'
import { searchProductsByReference } from '@/api/produits'
import { useAuthStore } from '@/stores/auth'

interface Props {
  isVisible: boolean
  fiche: LookbookFicheDisplay | null
  isSaving?: boolean
}

interface Emits {
  (e: 'update:isVisible', value: boolean): void
  (e: 'save', data: { 
    fiche: LookbookFicheDisplay
    file?: File
    products: Produit[]
    descriptions: {
      francais: string
      anglais: string
      espagnol: string
      italien: string
    }
  }): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LookbookFicheEditModal',
  props: {
    isVisible: { type: Boolean },
    fiche: {},
    isSaving: { type: Boolean, default: false }
  },
  emits: ["update:isVisible", "save"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const authStore = useAuthStore()
const fileInput = ref<HTMLInputElement>()
const selectedFile = ref<File | undefined>(undefined)
const multilingualTextareaRef = ref<InstanceType<typeof MultilingualTextarea> | null>(null)

// Variables locales pour v-model
const localSelectedProducts = ref<Produit[]>([])
const localDescriptions = ref({
  francais: '',
  anglais: '',
  espagnol: '',
  italien: ''
})

// Synchroniser avec la fiche quand elle change et charger les produits
watch(() => props.fiche, async (newFiche) => {
  if (newFiche) {
    localDescriptions.value = { ...newFiche.description }
    localSelectedProducts.value = []
    
    // Charger les produits associés
    try {
      const ficheProduits = await getFicheProduits(newFiche.id)
      
      if (ficheProduits.length > 0) {
        const vendeurId = authStore.user?.id_vendeur
        if (vendeurId) {
          const productsPromises = ficheProduits.map(fp => 
            searchProductsByReference(vendeurId, fp.id_produit.toString(), 1)
          )
          const productsResults = await Promise.all(productsPromises)
          localSelectedProducts.value = productsResults
            .filter(results => results.length > 0)
            .map(results => results[0])
        }
      }
    } catch (error) {
      // Erreur silencieuse - les produits de la fiche restent vides
    }
  }
}, { immediate: true })

const handleFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    const file = target.files[0]
    
    // Validation du type de fichier
    if (!file.type.startsWith('image/jpeg')) {
      alert('Seuls les fichiers JPEG sont autorisés')
      target.value = ''
      return
    }
    
    // Validation de la taille (2 Mo max)
    const maxSize = 2 * 1024 * 1024
    if (file.size > maxSize) {
      alert('Le fichier est trop volumineux. Taille maximale : 2 Mo')
      target.value = ''
      return
    }
    
    selectedFile.value = file
  }
}

const handleSave = async () => {
  if (!props.fiche) return

  // Auto-traduction de secours : si l'utilisateur a rempli une description
  // mais n'a pas cliqué sur "AI Traduction" (certaines langues vides),
  // on traduit automatiquement avant l'enregistrement.
  if (multilingualTextareaRef.value?.needsAutoTranslate?.()) {
    await multilingualTextareaRef.value.autoTranslate()
  }

  emit('save', {
    fiche: props.fiche,
    file: selectedFile.value,
    products: localSelectedProducts.value,
    descriptions: localDescriptions.value
  })
}

const handleClose = () => {
  emit('update:isVisible', false)
  // Réinitialiser
  selectedFile.value = undefined
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

// Réinitialiser quand le modal se ferme
watch(() => props.isVisible, (newVal) => {
  if (!newVal) {
    selectedFile.value = undefined
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalComp, {
    isVisible: _ctx.isVisible,
    "onUpdate:isVisible": handleClose,
    onConfirm: handleSave,
    title: "Modifier l'image",
    size: "lg"
  }, {
    header: _withCtx(() => [
      _createElementVNode("h3", _hoisted_1, "Lookbook #" + _toDisplayString(_ctx.fiche?.id), 1)
    ]),
    body: _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        _cache[5] || (_cache[5] = _createElementVNode("div", { class: "text-sm text-gray-600" }, [
          _createElementVNode("p", null, "Pour un affichage optimal de vos lookbook, la dimension de la photo ne doit pas dépasser 1600px en hauteur et en largeur, et doit être inférieur à 2Mo.")
        ], -1)),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("input", {
              ref_key: "fileInput",
              ref: fileInput,
              type: "file",
              accept: "image/jpeg",
              onChange: handleFileChange,
              class: "hidden"
            }, null, 544),
            _createElementVNode("button", {
              type: "button",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (fileInput.value?.click())),
              class: "inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            }, " Choisir un fichier "),
            _createElementVNode("span", _hoisted_5, _toDisplayString(selectedFile.value ? selectedFile.value.name : 'Aucun fichier choisi'), 1)
          ]),
          (_ctx.fiche?.image_url)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                _cache[3] || (_cache[3] = _createElementVNode("label", { class: "text-sm font-medium text-gray-700" }, "Aperçu de l'image actuelle :", -1)),
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("img", {
                    src: _ctx.fiche.image_url,
                    alt: "Aperçu",
                    class: "max-w-full h-auto rounded"
                  }, null, 8, _hoisted_8)
                ])
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_9, [
          _createElementVNode("div", _hoisted_10, [
            _cache[4] || (_cache[4] = _createElementVNode("div", { class: "flex items-center gap-2" }, [
              _createElementVNode("i", { class: "fas fa-info-circle text-blue-600" }),
              _createElementVNode("span", { class: "text-sm font-medium text-blue-900" }, "Informations de la fiche")
            ], -1)),
            _createElementVNode("span", _hoisted_11, "#" + _toDisplayString(_ctx.fiche?.id), 1)
          ])
        ]),
        _createVNode(ProductAutocomplete, {
          modelValue: localSelectedProducts.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((localSelectedProducts).value = $event)),
          label: "Liée des références à cette photo de votre lookbook :"
        }, null, 8, ["modelValue"]),
        _createVNode(MultilingualTextarea, {
          ref_key: "multilingualTextareaRef",
          ref: multilingualTextareaRef,
          modelValue: localDescriptions.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((localDescriptions).value = $event)),
          title: "Description du look :"
        }, null, 8, ["modelValue"])
      ])
    ]),
    footer: _withCtx(() => [
      _createElementVNode("button", {
        onClick: handleClose,
        class: "px-4 py-2 bg-gray-300 text-gray-800 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300"
      }, " Annuler "),
      _createElementVNode("button", {
        onClick: handleSave,
        disabled: _ctx.isSaving,
        class: "px-4 py-2 bg-blue-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      }, [
        (_ctx.isSaving)
          ? (_openBlock(), _createElementBlock("i", _hoisted_13))
          : _createCommentVNode("", true),
        _cache[6] || (_cache[6] = _createTextVNode(" Enregistrer "))
      ], 8, _hoisted_12)
    ]),
    _: 1
  }, 8, ["isVisible"]))
}
}

})